import { renderCell, renderHeader } from '../components/table';
import { useMemo, useContext } from 'react';
import { Flex, Box, Center, Spinner } from '@chakra-ui/react';
import { Wrapper } from '../components/Wrapper';
import { TitleWithTooltip } from '../components/title-with-tooltip';
import { BreakdownBy } from '../components/breakdown-by';
import { useDispatch, useSelector } from 'react-redux';
import { applyGroup } from '@/store/atsReports/contacts';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { useKFlags } from '@/hooks/use-k-flags';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components';
import { EmptyReportChart } from '@/pages/reports/custom-reports/components/ReportCharts/EmptyReportChart';

interface Option {
  label: string;
  id: number | string;
}

const GROUP_BY = [
  { id: null, label: 'Time' },
  { id: 'contact_source', label: 'Source (on contact)' },
  { id: 'contact_credited_to', label: 'Credited to (on contact)' },
];

const BREAKDOWN_BY = [
  { id: null, label: 'None' },
  { id: 'contact_source', label: 'Source (on contact)' },
  { id: 'contact_credited_to', label: 'Credited to (on contact)' },
];

export const TableView = ({
  groupBy1 = undefined,
  groupBy2 = undefined,
  customData = undefined,
  isReadOnly = false,
  isPrintView = false,
  customBreakdownChange = undefined,
  renderTitle = undefined,
  onDrillDownOpen = (data) => {},
  isStaticReport = false,
  onSortChange = (sortState) => {},
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  feReportsV3 = false,
  isFullScreen = false,
  isFilterApplied = false,
}) => {
  const dispatch = useDispatch();
  const { consumer } = useContext(ReportSelectionContext);

  const reduxState = useSelector((state: any) => state.contactsReports);
  const {
    table: reduxData,
    group: {
      group_by_1: reduxGroupBy = null,
      group_by_2: reduxBreakdownBy = null,
    } = {},
    isTableLoading: reduxIsLoading,
  } = reduxState || {};

  const dataToUse = customData || (feReportsV3 ? [] : reduxData) || [];
  const isDataEmpty = isReportTableEmpty(dataToUse, 'contacts_created');
  const isTableLoading = feReportsV3 ? false : reduxIsLoading;

  const { feDrilldown } = useKFlags();
  const { handleDrillDown } = useDrillDownHandler(feDrilldown, onDrillDownOpen);
  const getGroupValue = (value: any, fallbackValue: any) =>
    feReportsV3 ? value : value || fallbackValue;

  const drilldownGroup = {
    group_by_1: getGroupValue(groupBy1, reduxGroupBy),
    group_by_2: getGroupValue(groupBy2, reduxBreakdownBy),
  };

  const columns = useMemo(() => {
    const header = feReportsV3
      ? groupBy1 || GROUP_BY?.[0]
      : groupBy1 || reduxGroupBy || GROUP_BY?.[0];
    return [
      renderHeader({
        accessor: 'header',
        title: header?.label,
        width: 300,
        sort: true,
        isReadOnly,
        renderFooter: () => {
          return <KText size="sm-semi-bold">Total</KText>;
        },
        columnConfig: false,
      }),
      renderCell({
        accessor: 'contacts_created',
        title: 'Contacts',
        width: 300,
        sort: true,
        render: (_, row) => {
          return (
            <Flex
              _hover={{ textDecoration: feDrilldown ? 'underline' : 'none' }}
              cursor={feDrilldown ? 'pointer' : 'default'}
              onClick={() => {
                handleDrillDown(
                  row,
                  'Contacts',
                  false,
                  drilldownGroup,
                  null,
                  row?.contacts_created
                );
              }}
            >
              {row?.contacts_created}
            </Flex>
          );
        },
        renderFooter: (info) => {
          const row = info?.rows?.[0]?.original;
          return (
            <Flex
              _hover={{ textDecoration: feDrilldown ? 'underline' : 'none' }}
              cursor={feDrilldown ? 'pointer' : 'default'}
              onClick={() =>
                handleDrillDown(
                  row,
                  'Contacts',
                  true,
                  drilldownGroup,
                  null,
                  row?.contacts_created_total
                )
              }
            >
              <KText size="sm-semi-bold">{row?.contacts_created_total}</KText>
            </Flex>
          );
        },
      }),
    ];
  }, [groupBy1, groupBy2, reduxGroupBy, reduxBreakdownBy, feReportsV3]); //eslint-disable-line

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  const handleBreakdownChange = (key: string, option: Option) => {
    switch (key) {
      case 'group_by_1':
        dispatch(applyGroup({ key: 'group_by_1', value: option }));

        break;
      case 'group_by_2':
        dispatch(applyGroup({ key: 'group_by_2', value: option }));
        break;
      default:
        break;
    }
  };

  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: GROUP_BY,
      selected: feReportsV3
        ? groupBy1 || GROUP_BY[0]
        : groupBy1 || reduxGroupBy || GROUP_BY[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: BREAKDOWN_BY,
      selected: feReportsV3
        ? groupBy2 || BREAKDOWN_BY[0]
        : groupBy2 || reduxBreakdownBy || BREAKDOWN_BY[0],
    },
  ];

  if (isFilterApplied) {
    return <EmptyReportChart text="Filters not applicable" subText="The applied filters are not applicable for this block" />;
  }

  return (
    <Wrapper role="group" isReadOnly={isReadOnly || isStaticReport}>
      {consumer === 'DEFAULT' && (
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          position="sticky"
          top="0"
          zIndex="6"
          bg="white"
        >
          {renderTitle ? (
            renderTitle?.()
          ) : (
            <Box flex="1">
              <TitleWithTooltip
                label="Contacts breakdown"
                metricDefinition="Comprehensive breakdown of the contact data"
              />
            </Box>
          )}
          <Flex
            gap="8px"
            mr={
              isPrintView
                ? '0'
                : isDataEmpty
                ? '15px'
                : isEditMode
                ? '55px'
                : '35px'
            }
            mb="12px"
          >
            {breakdownConfig.map(({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) =>
                  customBreakdownChange
                    ? customBreakdownChange(key, option)
                    : handleBreakdownChange(key, option)
                }
              />
            ))}
          </Flex>
        </Flex>
      )}
      <EmptyDataWrapper data={dataToUse} field="contacts_created">
        {isTableLoading ? (
          <Center minHeight="400px">
            <Spinner color="primary-800" size="sm" />
          </Center>
        ) : (
          <Table
            tableId="contacts-table"
            skipLocalStorage={skipLocalStorage}
            defaultSortBy={[{ id: 'header', desc: false }]}
            isColumnLevelSorting
            enableColumnConfig={isEditMode}
            tableColStyles={tableColStyles}
            columns={columns}
            data={dataToUse}
            enableFooter={true}
            layoutScrollContainerStyles="c-data-table wide-content table-container"
            enableExpanded={true}
            layoutStyles={{
              borderBottom: 'none',
            }}
            getTableState={handleSortChange}
            onTableColDataChange={onTableColDataChange}
            initialTableColData={initialTableColData}
          />
        )}
      </EmptyDataWrapper>
    </Wrapper>
  );
};
